import { createActionBarEffect } from '@databases/action-bar/selections-effect';
import { collabStateManager } from '@databases/collab-state-manager';
import { createContextMenuEffect } from '@databases/context-menu/context-menu-effect';
import {
  getCachedViewSnapshotForSelections,
  getVersionId,
} from '@databases/database-state';
import { createViewStateSubscription } from '@databases/database-state/state/view';
import { key, notify } from '@databases/ping-registry';
import { type PluginKey } from '@databases/plugins';
import { isPluginEnabled } from '@databases/plugins/helper';
import { LegacyReduxSelectionsReader } from '@databases/redux/cellSelections/legacy-selections-reader';
import cellSelectionsSlice from '@databases/redux/cellSelections/slice';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { getDatabaseWithCurrentView } from '@databases/redux/database-view';
import { appStore } from '@databases/redux/store';
import { ReduxDatabaseAdapter } from '@databases/redux-database-adapter';
import {
  createCheckboxEffect,
  createPresenceEffect,
  createViewChangesInput,
  createYjsChangesInput,
  initializeSelectionsMachine,
  type SelectionsMachine,
} from '@databases/selections';
import {
  LegacyReduxAdapter,
  SelectionsApi,
  SelectionsApiAdapter,
  SelectionsApiReader,
} from '@databases/selections-api';
import { type DatabaseAdapter } from '@databases/selections-api-types';
import { container, getService } from '@databases/service-container';
import { createCellEditingEffect } from '@databases/value/cell-edit-effect';

import { getExperimentValue } from '@canvas/client-feature-flags';

import { createCollabStructuralChangeSubscription } from './create-collab-structural-change-subscription';

function registerLegacySelectionsDependencies(contentId: string) {
  container.registerSingleton(
    'SelectionsActions',
    () =>
      new LegacyReduxAdapter({
        contentId,
        dispatch: appStore.dispatch,
        sliceActions: cellSelectionsSlice.actions,
      })
  );

  const getState = appStore.getState;
  const getContentId = () => contentId;
  const getDatabaseSnapshot = () => {
    const database = getDatabaseWithCurrentView(getState(), { contentId });
    return {
      entries: database.entries,
      fields: database.fields,
    };
  };

  container.registerSingleton(
    'SelectionsReader',
    () =>
      new LegacyReduxSelectionsReader({
        getState,
        getContentId,
        getDatabaseSnapshot,
      })
  );
}

/**
 * Initializes the selections machine behind a feature gate
 * with a listener on databaseContentIdChanged to destroy
 * and rebuild it, so no highlights or state exists between
 * changing the content id.
 */
export function initializeRecyclableSelectionsMachine(initialContentId: string) {
  const experimentIsEnabled = getExperimentValue(
    'databasesSelectionsPerformance',
    'isEnabled',
    {
      fireExperimentExposure: false,
    }
  );

  if (!experimentIsEnabled) {
    registerLegacySelectionsDependencies(initialContentId);

    const eventBus = getService('EventBus');

    eventBus.on('databaseContentIdChanged', (data) => {
      registerLegacySelectionsDependencies(data.contentId);
    });

    return;
  }

  let machine: SelectionsMachine;
  let setup: () => VoidFunction;
  let destroy: VoidFunction;
  let selectionsApi: SelectionsApi;
  let databaseAdapter: DatabaseAdapter;

  function initialize(currentContentId: string) {
    const selectionsApiFactory = () => selectionsApi;
    const databaseAdapterFactory = () => databaseAdapter;
    const selectionsActionsFactory = () => getService('SelectionsActions');

    const collabManager = collabStateManager.getCollabManager(currentContentId);

    const inputs = [
      (m: SelectionsMachine) => createViewChangesInput(m, createViewStateSubscription),
    ];

    if (collabManager) {
      inputs.push((m: SelectionsMachine) =>
        createYjsChangesInput(m, createCollabStructuralChangeSubscription(collabManager))
      );
    }

    ({ setup, machine } = initializeSelectionsMachine({
      highlightsController: getService('TableHighlightsController'),
      inputs,
      effects: [
        () =>
          createActionBarEffect({
            selectionsApiFactory,
            contentId: currentContentId,
            databaseAdapterFactory,
            panelManager: getService('PanelManager'),
          }),
        () =>
          createContextMenuEffect({
            selectionsApiFactory,
            databaseAdapterFactory,
            contentId: currentContentId,
            formatMessage: getService('Intl').formatMessage,
            panelManager: getService('PanelManager'),
            selectionsActionsFactory,
            currentViewIdFactory: () => getCachedViewSnapshotForSelections().id,
          }),
        () =>
          createCellEditingEffect({
            selectionsApiFactory: () => selectionsApi,
          }),
        () =>
          createPresenceEffect({
            selectionsApiFactory,
            collabStateManager,
            contentId: currentContentId,
          }),
        () =>
          createCheckboxEffect({
            getTotalEntryCount: () => databaseAdapter.getVisibleEntryIds().length,
            getVisibleEntryIds: () => databaseAdapter.getVisibleEntryIds(),
            resolveRowIndexToEntryId: (rowIndex: number) =>
              databaseAdapter.getVisibleEntryIds()[rowIndex],
            notifyRowSelected: (entryId: string, selected: boolean) => {
              const rowKey = key('row', { entryId });
              notify(rowKey, 'selected', selected);
            },
            notifyHeaderCheckboxState: (state) => {
              const headerKey = key('headerCheckbox', {});
              notify(headerKey, 'state', state);
            },
          }),
      ],
    }));

    databaseAdapter = new ReduxDatabaseAdapter({
      getContentId: () => currentContentId,
      getDatabaseSnapshot: (id) =>
        selectDatabaseDraftByContentId(appStore.getState(), {
          contentId: id,
          includeDeleted: false,
          viewSettings: getCachedViewSnapshotForSelections().settings,
        }),
      getVersionId,
      getState: appStore.getState,
      pluginChecker: {
        isEnabled: (key) => isPluginEnabled(key as PluginKey),
      },
    });

    selectionsApi = new SelectionsApi({ machine, adapter: databaseAdapter });

    container.registerSingleton('SelectionsActions', () => {
      return new SelectionsApiAdapter({
        selectionsApi,
      });
    });

    container.registerSingleton('SelectionsReader', () => {
      return new SelectionsApiReader({
        selectionsApi,
        adapter: databaseAdapter,
      });
    });

    destroy = setup();
  }

  initialize(initialContentId);

  const rebuildSelectionsMachineState = (data: { contentId: string }) => {
    destroy();

    initialize(data.contentId);
  };

  const eventBus = getService('EventBus');

  eventBus.on('databaseContentIdChanged', rebuildSelectionsMachineState);
}
